'use client';
import React, { useEffect } from 'react';
import Head from 'next/head';
import Script from 'next/script';
import { useChatbotVisibility } from '@/hooks/useChatbotVisibility';
import useFeature from '@/hooks/useFeature';

const Ada = () => {
  const enableAdaGreeting = Boolean(
    useFeature('retention-enable-ada-greeting'),
  );
  const adaEnvironment = useFeature('sub-squad-ada-environment');

  // EU/UK config
  const region = process.env.NEXT_PUBLIC_REGION;
  const isEU = region !== 'US';
  const cluster = 'eu';

  const { isDisabled } = useChatbotVisibility();
  const adaGreetingId = process.env.NEXT_PUBLIC_ADA_GREETING_ID || '';

  const loveveryUserJwt = localStorage.getItem('le-user-jwt');
  const user = localStorage.getItem('user');
  const firstName = user ? JSON.parse(user)?.attributes.name : '';
  const isDrawerOpen =
    localStorage.getItem('ada-embed_is-drawer-open') === 'true';

  const initializeAdaBot = async () => {
    if (!window.adaEmbed) {
      console.error('Ada script is not available.');
      return;
    }

    const adaSettings = {
      handle: adaEnvironment,
      ...(isEU && { cluster }),
      metaFields: {
        location_origin: window.location.origin,
        ...(firstName && { first_name: firstName }),
      },
      sensitiveMetaFields: {
        ...(loveveryUserJwt && { lovevery_user_jwt: loveveryUserJwt }),
      },
    };

    window.adaEmbed.start(adaSettings);

    if (firstName) {
      window.adaEmbed.setMetaFields({
        first_name: firstName,
      });
    }

    if (isDrawerOpen) {
      await window.adaEmbed.toggle();
    }

    if (loveveryUserJwt) {
      window.adaEmbed.setSensitiveMetaFields({
        lovevery_user_jwt: loveveryUserJwt,
      });
    }
  };

  useEffect(() => {
    if (loveveryUserJwt && isDrawerOpen && enableAdaGreeting) {
      window.adaEmbed?.triggerAnswer(adaGreetingId);
    }
  }, [loveveryUserJwt, isDrawerOpen, enableAdaGreeting, adaGreetingId]);

  if (isDisabled || !enableAdaGreeting) {
    return null;
  }

  return (
    <>
      <Head>
        <style>
          {`
          #ada-button-frame {
            bottom: 1rem !important;
          }
        `}
        </style>
      </Head>
      <Script
        async
        data-testid="ada"
        id="__ada"
        data-lazy
        data-handle={adaEnvironment}
        data-cluster={isEU ? cluster : undefined}
        src="https://static.ada.support/embed2.js"
        onLoad={initializeAdaBot}
      />
    </>
  );
};

export default React.memo(Ada);
